import { useMemo } from 'react';

import { ErrorLike, gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { selectAccountId, selectBrainId } from '@/redux/session/selectors';
import { getAnalyticsRangeUTC } from '@/util/analytics';

interface DebtCollectionInsightsRow {
  total_sessions_promise_to_pay: number;
  sessions_with_promise_to_pay: number;
  promise_to_pay_rate: number | null;
  total_sessions_dispute: number;
  sessions_with_dispute: number;
  dispute_rate: number | null;
  total_debt_amount: number | null;
}

interface DebtCollectionInsightsData {
  debt_collection_insights: DebtCollectionInsightsRow[];
}

interface DebtCollectionInsightsResult {
  isLoading: boolean;
  error?: ErrorLike;
  promiseToPayRate: number | null;
  totalSessionsPromiseToPay: number;
  sessionsWithPromiseToPay: number;
  disputeRate: number | null;
  totalSessionsDispute: number;
  sessionsWithDispute: number;
  totalDebtAmount: number | null;
}

interface UseDebtCollectionInsightsProps {
  startDate: string;
  endDate: string;
  skip?: boolean;
}

export const DEBT_COLLECTION_INSIGHTS = gql`
  query GetDebtCollectionInsights(
    $accountId: uuid
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
  ) {
    debt_collection_insights(
      args: {
        account_id: $accountId
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
      }
    ) {
      total_sessions_promise_to_pay
      sessions_with_promise_to_pay
      promise_to_pay_rate
      total_sessions_dispute
      sessions_with_dispute
      dispute_rate
      total_debt_amount
    }
  }
`;

export const useDebtCollectionInsights = ({
  startDate,
  endDate,
  skip = false,
}: UseDebtCollectionInsightsProps): DebtCollectionInsightsResult => {
  const accountId = useSelector(selectAccountId);
  const brainId = useSelector(selectBrainId);

  const variables = useMemo(
    () => ({
      ...getAnalyticsRangeUTC(startDate, endDate),
      accountId,
      brainIds: brainId ? `{${brainId}}` : undefined,
    }),
    [accountId, brainId, startDate, endDate]
  );

  const { data, loading, error } = useQuery<DebtCollectionInsightsData>(DEBT_COLLECTION_INSIGHTS, {
    variables,
    skip: skip || !accountId,
  });

  const result = data?.debt_collection_insights?.[0];

  return {
    isLoading: loading,
    error,
    promiseToPayRate: result?.promise_to_pay_rate ?? null,
    totalSessionsPromiseToPay: result?.total_sessions_promise_to_pay ?? 0,
    sessionsWithPromiseToPay: result?.sessions_with_promise_to_pay ?? 0,
    disputeRate: result?.dispute_rate ?? null,
    totalSessionsDispute: result?.total_sessions_dispute ?? 0,
    sessionsWithDispute: result?.sessions_with_dispute ?? 0,
    totalDebtAmount: result?.total_debt_amount ?? null,
  };
};
